import React from 'react';
import { Card, CardContent } from './ui/card';
import { Database, Workflow, Server, Layers, CheckCircle, Rocket } from 'lucide-react';

const serviceImages = {
  automation: [
    'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/36496927/pexels-photo-36496927.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  ],
  fullCycle: [
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c',
    'https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg'
  ],
  gameDev: [
    'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxWUiUyMGdhbWluZ3xlbnwwfHx8fDE3NzUxNjMxNjF8MA&ixlib=rb-4.1.0&q=85',
    'https://images.pexels.com/photos/18966450/pexels-photo-18966450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  ]
};

const ServiceCard = ({ title, description, features, images, icon: Icon, index }) => {
  const isReversed = index % 2 !== 0;

  return (
    <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 items-center`}>
      {/* Images */}
      <div className="w-full lg:w-1/2 relative">
        <div className="relative h-[400px] rounded-2xl overflow-hidden">
          <img 
            src={images[0]} 
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
          
          {/* Floating second image */}
          <div className="absolute bottom-4 right-4 w-48 h-32 rounded-lg overflow-hidden border-4 border-slate-800 shadow-2xl transform hover:scale-105 transition-transform">
            <img 
              src={images[1]} 
              alt={`${title} detail`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full lg:w-1/2 space-y-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600/20 rounded-lg border border-blue-500/30">
            <Icon className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-3xl font-bold text-white">{title}</h3>
        </div>
        
        <p className="text-lg text-gray-300 leading-relaxed">
          {description}
        </p>

        <ul className="space-y-3">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
              <span className="text-gray-300">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const ServicesSection = ({ t }) => {
  const services = [
    {
      title: t.services.automation.title,
      description: t.services.automation.description,
      features: t.services.automation.features,
      images: serviceImages.automation,
      icon: Database
    },
    {
      title: t.services.fullCycle.title,
      description: t.services.fullCycle.description,
      features: t.services.fullCycle.features,
      images: serviceImages.fullCycle,
      icon: Rocket
    },
    {
      title: t.services.gameDev.title,
      description: t.services.gameDev.description,
      features: t.services.gameDev.features,
      images: serviceImages.gameDev,
      icon: Layers
    }
  ];

  return (
    <section id="services" className="py-20 lg:py-32 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {t.services.title}
          </h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto" />
        </div>

        <div className="space-y-24">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
